import React from "react";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";

/**
 * The call room (Chats.js / TextChat.js) had no error boundary at all, so an
 * unhandled render-time exception — most likely from OpenTok session/publisher
 * refs left in an inconsistent state after a reconnect — unmounted the whole
 * tree with nothing to catch it, producing a completely blank/white screen
 * instead of any redirect or message.
 */
class CallErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Call room crashed:", error, info);
    // Release camera/mic even though the call UI is gone, so a crash never
    // leaves the browser's recording indicator stuck on.
    try {
      stopAllRegisteredMedia();
    } catch (_) {
      /* ignore */
    }
  }

  handleReturn = () => {
    try {
      const appointmentId =
        JSON.parse(localStorage.getItem("appointmentData") || "{}")?.appointmentId || "";
      window.location.href = appointmentId
        ? `/appointments-details/${appointmentId}`
        : "/appointments";
    } catch (_) {
      window.location.href = "/appointments";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950 p-4 text-white">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-7 text-center shadow-2xl">
            <h2 className="text-xl font-bold">Something went wrong with this call</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/75">
              The call screen ran into a problem. Your camera and microphone have been released.
            </p>
            <button
              type="button"
              onClick={this.handleReturn}
              className="mt-5 w-full rounded-lg bg-prontopsy-blue px-4 py-2 text-sm font-bold text-white">
              Return to my appointments
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default CallErrorBoundary;
